import React, { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Container, Divider, TextField } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'

import { Layout } from 'ui/layout/Layout'
import { AppBarPatient } from 'ui/partials/AppBar'
import { H2, Caption, Text1 } from 'ui/shorthands/Texts'
import Confirmation from 'ui/partials/Confirmation'
import information from 'config/information'
import placeholder from 'config/placeholder'
import type { PreReservation } from 'types/med'
import type { MedicalInstitutionProps } from 'types/applicant'
import { toDateDayTimeStringFromDateTimeString } from 'ui/utils/AvailableDateTimes'
import { VerificationTypes } from 'types/common'
import validationSchema from 'ui/shorthands/validationSchema'
import * as yup from 'yup'
import {
  ReceptionEmailSendFormProps,
  ReceptionSmsSendFormProps,
} from 'types/applicant'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'
import { ApiErrorWithMessage, OfflineError } from 'app/client'

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(2),
    padding: 0,
    maxWidth: 488,
  },
  departmentsTitle: {
    display: 'block',
    fontWeight: 'bold',
    padding: theme.spacing(1, 0),
    color: theme.palette.text.secondary,
  },
  departments: {
    padding: theme.spacing(1, 2.5, 0),
    margin: 0,
    li: {
      paddingBottom: theme.spacing(0.5),
    },
  },
  department: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  header: {
    lineHeight: theme.spacing(5),
  },
  subHeader: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  caption: {
    display: 'block',
    paddingBottom: theme.spacing(1),
  },
  cols: {
    padding: theme.spacing(4),
    '&:first-child': {
      paddingBottom: theme.spacing(3),
    },
    '&:last-child': {
      paddingTop: theme.spacing(3),
    },
  },
  preReservationDetails: {
    paddingTop: theme.spacing(3),
  },
  preReservationDetailsText: {
    color: theme.palette.text.secondary,
    fontWeight: 700,
  },
  confirmation: {
    paddingTop: theme.spacing(1),
  },
  submitWrapper: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

export type ReceptionProps = {
  med: MedicalInstitutionProps
  onSubmit: (
    data: ReceptionEmailSendFormProps | ReceptionSmsSendFormProps
  ) => Promise<void>
  preReservation?: PreReservation
}

const Reception: React.FC<ReceptionProps> = ({
  med,
  onSubmit,
  preReservation,
}: ReceptionProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [confirmed, setConfirmed] = useState<boolean>(false)

  const [value, setValue] = useState<string>('')
  const [validationErrorMessage, setValidationErrorMessage] = useState<
    string | undefined
  >()
  const [apiErrorMessage, setApiErrorMessage] = useState<string | undefined>()

  const [isValid, setIsValid] = useState<boolean>(false)
  const [verificationType, setVerificationType] = useState<
    VerificationTypes | undefined
  >(undefined)

  // NOTE: 入力によってverificationTypeをセットしてしバリデーションを続行するのでhook-form不使用
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target?.value
    setValue(value)
    setValidationErrorMessage(undefined)
    const emailSchema = yup.object().shape({
      value: validationSchema.email(),
    })
    const phoneSchema = yup.object().shape({
      value: validationSchema.phone(),
    })
    emailSchema
      .validate({ value })
      .then(() => {
        setIsValid(true)
        setVerificationType('email')
      })
      .catch(() => {
        phoneSchema
          .validate({ value })
          .then(() => {
            setIsValid(true)
            setVerificationType('sms')
          })
          .catch(() => {
            setIsValid(false)
          })
      })
  }

  const handleBlur = () => {
    if (!!value && !isValid) {
      setValidationErrorMessage(t('validation.正しい形式にしてください'))
      return
    }
    if (!value) {
      setValidationErrorMessage(t('validation.入力してください'))
    }
  }

  const handleConfirm = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmed(event.target.checked)
  }

  const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
    setIsValid(false)
    let data
    switch (verificationType) {
      case 'email':
        data = { email: value }
        break
      case 'sms':
        data = { phone: value }
        break
    }
    data &&
      onSubmit(data)
        .catch((e) => {
          if (e instanceof OfflineError) {
            setApiErrorMessage(
              t(
                'errors.ネットワークに繋がっていません。通信環境を確認してください。'
              )
            )
            return
          }
          if (
            e instanceof ApiErrorWithMessage &&
            e.has({
              type: 'server_error',
              key: ['email', 'sms'],
            })
          ) {
            setApiErrorMessage(
              t('errors.送信に失敗しました。再度送信ボタンを押してください。')
            )
            return
          }
          throw Error('unknown server_error')
        })
        .catch(() => {
          setApiErrorMessage(t('errors.送信に失敗しました。'))
        })
        .finally(() => setIsValid(true))
    event.preventDefault()
  }

  const errorModal = (
    <Modal
      open={!!apiErrorMessage}
      handleClose={() => setApiErrorMessage(undefined)}
    >
      <ConfirmContent
        title={apiErrorMessage}
        onSubmit={() => setApiErrorMessage(undefined)}
        submitLabel={t('閉じる')}
      />
    </Modal>
  )

  return (
    <Layout header={<AppBarPatient>{med.name}</AppBarPatient>} showFooter>
      {errorModal}
      <Container className={classes.container} maxWidth="sm">
        <div className={classes.cols}>
          {preReservation ? (
            <>
              <H2>{t('仮予約情報')}</H2>
              <div className={classes.preReservationDetails}>
                <Caption className={classes.departmentsTitle}>
                  {t('受付科目')}
                </Caption>
                <Text1 className={classes.preReservationDetailsText}>
                  {preReservation.provisional_reservation_department_id
                    ? med.departments.find(
                        ({ id }) =>
                          id ===
                          preReservation.provisional_reservation_department_id
                      )?.name || t('指定なし')
                    : t('指定なし')}
                </Text1>
              </div>
              <div className={classes.preReservationDetails}>
                <Caption className={classes.departmentsTitle}>
                  {t('予約日時')}
                </Caption>
                <Text1 className={classes.preReservationDetailsText}>
                  {preReservation.provisional_reservation_datetime_start
                    ? `${toDateDayTimeStringFromDateTimeString(
                        preReservation.provisional_reservation_datetime_start
                      )}〜（${t('日本時間')}）`
                    : t('指定なし')}
                </Text1>
              </div>
            </>
          ) : (
            <>
              <Caption className={classes.departmentsTitle}>
                {t('受付科目')}
              </Caption>
              <ul className={classes.departments}>
                {med.departments.map((d) => (
                  <li key={d.name}>
                    <Text1 className={classes.department}>{d.name}</Text1>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <Divider />
        <div className={classes.cols}>
          <form onSubmit={submitHandler}>
            <H2 className={classes.header}>
              {preReservation
                ? t('詳細を入力し、手続きを完了しましょう')
                : t('予約をはじめましょう')}
            </H2>
            <h3 className={classes.subHeader}>
              {t('メールアドレスまたは電話番号を入力してください')}
            </h3>
            <Caption className={classes.caption}>
              {t(
                '携帯電話会社のキャリアメール（末尾がdocomo.ne.jp、ezweb.ne.jp、softbank.ne.jpなど）のアドレスは使用できません。'
              )}
            </Caption>
            <TextField
              inputProps={{
                'data-testid': 'email',
              }}
              variant="outlined"
              placeholder={placeholder().email_or_phone}
              name={'email'}
              error={!!validationErrorMessage}
              helperText={validationErrorMessage}
              fullWidth
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <div className={classes.confirmation}>
              <Confirmation
                checked={confirmed}
                onCheckboxClick={handleConfirm}
                termsUrl={information.personal.terms}
                privacyPolicyUrl={information.personal.privacyPolicy}
                leftTop
              />
              <div className={classes.submitWrapper}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!confirmed || !isValid}
                >
                  {t('送信')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Container>
    </Layout>
  )
}

export default Reception
